import { LocalNotifications, type ScheduleOptions } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import {
  formatExpirationLead,
  isWithinExpirationLead,
  readExpirationPreferences,
  type ExpirationPreferences,
} from '../haccp/expirationPreferences.js';

const DISMISS_KEY = 'niveo:expiration_notified';
const DISMISS_WINDOW_MS = 24 * 3_600_000;
const SCHEDULED_KEY = 'niveo:expiration_scheduled';

interface ExpiringLot {
  id: string;
  product: string;
  date: string;
}

function alreadyNotified(ids: string[]): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { ts: number; ids: string[] };
    if (Date.now() - parsed.ts > DISMISS_WINDOW_MS) return false;
    return ids.every((id) => parsed.ids.includes(id));
  } catch {
    return false;
  }
}

function markNotified(ids: string[]): void {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ ts: Date.now(), ids }));
  } catch {
    // ignore
  }
}

function readScheduledIds(): Set<string> {
  try {
    const raw = localStorage.getItem(SCHEDULED_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function persistScheduledIds(ids: Set<string>): void {
  try {
    localStorage.setItem(SCHEDULED_KEY, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

function notificationIdFor(lotId: string, offsetHours: number): number {
  let h = 0;
  const seed = `${lotId}@${offsetHours}`;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h % 2_000_000_000) + 1;
}

async function ensurePermission(): Promise<boolean> {
  const cur = await LocalNotifications.checkPermissions();
  if (cur.display === 'granted') return true;
  const req = await LocalNotifications.requestPermissions();
  return req.display === 'granted';
}

/**
 * Schedules local notifications at the configured lead times before expiration.
 * Idempotent: same (lotId, offset) is never scheduled twice across sessions.
 * Falls back to web Notification API on browsers without Capacitor runtime.
 */
export async function scheduleExpirationNotifications(
  lots: ExpiringLot[],
  preferences: ExpirationPreferences = readExpirationPreferences(),
  scopeId: string | null = null,
): Promise<void> {
  if (lots.length === 0 || preferences.reminderLeadHours.length === 0) return;

  if (Capacitor.isNativePlatform()) {
    if (!(await ensurePermission())) return;
    const scheduled = readScheduledIds();
    const toSchedule: ScheduleOptions['notifications'] = [];
    for (const lot of lots) {
      const expiresAt = Date.parse(lot.date);
      if (!Number.isFinite(expiresAt)) continue;
      for (const offset of preferences.reminderLeadHours) {
        const fireAt = new Date(expiresAt - offset * 3_600_000);
        if (fireAt.getTime() <= Date.now()) continue;
        const key = `${scopeId ?? 'device'}:${lot.id}@${offset}`;
        const legacyKey = `${lot.id}@${offset}`;
        if (scheduled.has(key)) continue;
        if (scheduled.has(legacyKey)) {
          scheduled.delete(legacyKey);
          scheduled.add(key);
          continue;
        }
        toSchedule.push({
          id: notificationIdFor(lot.id, offset),
          title: `Scadenza tra ${formatExpirationLead(offset)}`,
          body: `${lot.product} scade il ${lot.date}`,
          schedule: { at: fireAt, allowWhileIdle: true },
          extra: { lotId: lot.id },
        });
        scheduled.add(key);
      }
    }
    if (toSchedule.length > 0) {
      try {
        await LocalNotifications.schedule({ notifications: toSchedule });
        persistScheduledIds(scheduled);
      } catch {
        // schedule failed (permission revoked, OS limit) — leave for next tick
      }
    } else {
      persistScheduledIds(scheduled);
    }
    return;
  }

  // Web fallback: best-effort one-shot Notification.
  if (typeof Notification === 'undefined') return;
  const maxLeadHours = Math.max(...preferences.reminderLeadHours);
  const webLots = lots.filter((lot) => isWithinExpirationLead(lot.date, maxLeadHours));
  if (webLots.length === 0) return;
  const ids = webLots.map((l) => l.id).sort();
  if (alreadyNotified(ids)) return;
  let permission = Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch {
      return;
    }
  }
  if (permission !== 'granted') return;
  const body = webLots
    .slice(0, 3)
    .map((l) => `${l.product} — ${l.date}`)
    .join('\n')
    + (webLots.length > 3 ? `\n(+${webLots.length - 3} altri)` : '');
  try {
    new Notification('Scadenze imminenti', {
      body,
      icon: '/icon-192.png',
      tag: 'niveo-expirations',
      requireInteraction: false,
    });
    markNotified(ids);
  } catch {
    // ignore
  }
}

/**
 * Cancella soltanto le notifiche scadenza note a TrackEAT e le riprogramma
 * con le nuove soglie. Le notifiche di altri moduli restano intatte.
 */
export async function rescheduleExpirationNotifications(
  lots: ExpiringLot[],
  preferences: ExpirationPreferences,
  scopeId: string | null = null,
): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const scopePrefix = `${scopeId ?? 'device'}:`;
    const keysToCancel = [...readScheduledIds()].filter((key) => (
      key.startsWith(scopePrefix) || !key.includes(':')
    ));
    const ids = keysToCancel.map((key) => {
      const separator = key.lastIndexOf('@');
      const scopedLotId = separator >= 0 ? key.slice(0, separator) : key;
      const lotId = scopedLotId.startsWith(scopePrefix)
        ? scopedLotId.slice(scopePrefix.length)
        : scopedLotId;
      const offset = separator >= 0 ? Number(key.slice(separator + 1)) : 0;
      return notificationIdFor(lotId, offset);
    });
    if (ids.length > 0) {
      await LocalNotifications.cancel({
        notifications: [...new Set(ids)].map((id) => ({ id })),
      });
    }
  }
  const keptScheduledIds = new Set(
    [...readScheduledIds()].filter((key) => key.includes(':') && !key.startsWith(`${scopeId ?? 'device'}:`)),
  );
  persistScheduledIds(keptScheduledIds);
  try {
    localStorage.removeItem(DISMISS_KEY);
  } catch {
    // ignore
  }
  await scheduleExpirationNotifications(lots, preferences, scopeId);
}

/**
 * Deprecated alias for backwards compatibility with existing TodayPage call site.
 */
export const notifyExpirations = scheduleExpirationNotifications;
